/* eslint-disable no-new */
import { Accordion } from "./components/accordion/accordion.mjs";
import { Breadcrumbs } from "./components/breadcrumbs/breadcrumbs.mjs";
import { CodeBlock } from "./components/code-block/code-block.mjs";
import { CookieBanner } from "./components/cookie-banner/cookie-banner.mjs";
import { DateInputProgressive } from "./components/date-input/date-input.mjs";
import { ErrorSummary } from "./components/error-summary/error-summary.mjs";
import { FileInputDroppable } from "./components/file-input/file-input.mjs";
import { Footer } from "./components/footer/footer.mjs";
import { Gallery } from "./components/gallery/gallery.mjs";
import { GlobalHeader } from "./components/global-header/global-header.mjs";
import { Header } from "./components/header/header.mjs";
import { Picture } from "./components/picture/picture.mjs";
import { Sidebar } from "./components/sidebar/sidebar.mjs";
import { SkipLink } from "./components/skip-link/skip-link.mjs";
import { Tabs } from "./components/tabs/tabs.mjs";
import { TextInputPassword } from "./components/text-input/text-input.mjs";
import { TextAreaItemisedRows } from "./components/textarea/textarea.mjs";
import { checkTableForScroll, updateTimeElement } from "./lib/helpers.mjs";

const initAll = (options = {}) => {
  const { scope } = options;
  let $scope = document;
  if (scope) {
    if (scope instanceof HTMLElement) {
      $scope = scope;
    } else {
      throw new Error("Scope must be an HTMLElement");
    }
  }
  const $html = document.documentElement;
  $html.classList.add("tna-template--js-enabled");

  const onFirstTouch = () => {
    window.removeEventListener("touchstart", onFirstTouch);
    $html.classList.add("tna-template--touched");
  };
  window.addEventListener("touchstart", onFirstTouch);

  const onKeyDown = (event) => {
    if (event.key === "Tab") {
      $html.classList.add("tna-template--tabbed");
      $html.classList.remove("tna-template--clicked");
    }
  };
  window.addEventListener("keydown", onKeyDown);

  const onMouseDown = () => {
    $html.classList.add("tna-template--clicked");
    $html.classList.remove("tna-template--tabbed");
  };
  window.addEventListener("mousedown", onMouseDown);

  /*
   * ==========================================
   * Checks if widths of all tables on the page
   * are wider than their parent container, and
   * allowing horizontal scrolling if they are.
   * This is done both on load and on resize.
   * ==========================================
   */
  const $tableWrappers = $scope.querySelectorAll(".tna-table-wrapper");
  $tableWrappers.forEach(($tableWrapper) => checkTableForScroll($tableWrapper));
  window.addEventListener("resize", () => {
    $tableWrappers.forEach(($tableWrapper) =>
      checkTableForScroll($tableWrapper),
    );
  });

  // Remove this opt-in class in a later release
  if ($html.classList.contains("tna-template--enhance-time-elements")) {
    document.querySelectorAll("time[datetime]").forEach(updateTimeElement);
  }

  const $accordions = $scope.querySelectorAll('[data-module="tna-accordion"]');
  $accordions.forEach(($accordion) => {
    new Accordion($accordion);
  });

  const $breadcrumbs = $scope.querySelector('[data-module="tna-breadcrumbs"]');
  if ($breadcrumbs) {
    new Breadcrumbs($breadcrumbs);
  }

  const $codeBlocks = $scope.querySelectorAll('[data-module="tna-code-block"]');
  $codeBlocks.forEach(($codeBlock) => {
    new CodeBlock($codeBlock);
  });

  const $cookieBanner = $scope.querySelector(
    '[data-module="tna-cookie-banner"]',
  );
  if ($cookieBanner) {
    new CookieBanner($cookieBanner);
  }

  const $progressiveDateInputs = $scope.querySelectorAll(
    '[data-module="tna-date-input-progressive"]',
  );
  $progressiveDateInputs.forEach(($dateInput) => {
    new DateInputProgressive($dateInput);
  });

  const $errorSummary = $scope.querySelector(
    '[data-module="tna-error-summary"]',
  );
  if ($errorSummary) {
    new ErrorSummary($errorSummary).init();
  }

  const $fileInputs = $scope.querySelectorAll('[data-module="tna-file-input"]');
  $fileInputs.forEach(($fileInput) => {
    new FileInputDroppable($fileInput);
  });

  const $footer = $scope.querySelector('[data-module="tna-footer"]');
  if ($footer) {
    new Footer($footer);
  }

  const $galleries = $scope.querySelectorAll('[data-module="tna-gallery"]');
  $galleries.forEach(($gallery) => {
    new Gallery($gallery);
  });

  const $globalHeader = $scope.querySelector(
    '[data-module="tna-global-header"]',
  );
  if ($globalHeader) {
    new GlobalHeader($globalHeader);
  }

  const $header = $scope.querySelector('[data-module="tna-header"]');
  if ($header) {
    new Header($header);
  }

  const $pictures = $scope.querySelectorAll('[data-module="tna-picture"]');
  $pictures.forEach(($picture) => {
    new Picture($picture);
  });

  const $sidebar = $scope.querySelector('[data-module="tna-sidebar-sections"]');
  if ($sidebar) {
    const { scrollTopThreshold, disableHighlightSize } = $sidebar.dataset;
    new Sidebar($sidebar, { scrollTopThreshold, disableHighlightSize });
  }

  const $skipLinks = $scope.querySelectorAll('[data-module="tna-skip-link"]');
  $skipLinks.forEach(($skipLink) => {
    new SkipLink($skipLink);
  });

  const $tabs = $scope.querySelectorAll('[data-module="tna-tabs"]');
  $tabs.forEach(($tabModule) => {
    new Tabs($tabModule);
  });

  const $textAreaItemisedRows = $scope.querySelectorAll(
    '[data-module="tna-textarea-itemised-rows"]',
  );
  $textAreaItemisedRows.forEach(($textAreaWithItemisedRows) => {
    const { enhancedHint } = $textAreaWithItemisedRows.dataset;
    new TextAreaItemisedRows($textAreaWithItemisedRows, { enhancedHint });
  });

  const $textInputPasswords = $scope.querySelectorAll(
    '[data-module="tna-text-input-password"]',
  );
  $textInputPasswords.forEach(($textInputPassword) => {
    new TextInputPassword($textInputPassword);
  });

  window.matchMedia("print").addEventListener("change", (evt) => {
    if (evt.matches) {
      $scope
        .querySelectorAll(".tna-details__details:not([open])")
        .forEach(($element) => {
          $element.setAttribute("open", "");
          $element.dataset.wasClosed = "";
        });
      $scope
        .querySelectorAll(
          ".tna-accordion__content[hidden], .tna-picture__transcript[hidden]",
        )
        .forEach(($element) => {
          $element.removeAttribute("hidden");
          $element.dataset.wasClosed = "";
        });
    } else {
      $scope
        .querySelectorAll(".tna-details__details[data-was-closed]")
        .forEach(($element) => {
          $element.removeAttribute("open");
          delete $element.dataset.wasClosed;
        });
      $scope
        .querySelectorAll(
          ".tna-accordion__content[data-was-closed], .tna-picture__transcript[data-was-closed]",
        )
        .forEach(($element) => {
          $element.setAttribute("hidden", "");
          $element.dataset.wasClosed = "";
        });
    }
  });
};

export {
  initAll,
  Accordion,
  Breadcrumbs,
  CodeBlock,
  CookieBanner,
  DateInputProgressive,
  ErrorSummary,
  FileInputDroppable,
  Footer,
  Gallery,
  GlobalHeader,
  Header,
  Picture,
  Sidebar,
  SkipLink,
  Tabs,
  TextInputPassword,
  TextAreaItemisedRows,
  checkTableForScroll,
  updateTimeElement,
};
